import {
  BufferGeometry,
  Euler,
  Line as L,
  LineBasicMaterial,
  Mesh,
  Object3D,
  Scene,
  Vector3,
} from "three";
import { CSS3DObjectInput } from "../core/input/CSS3DObjectInput";
import { formatLength } from "../core/units/LengthUnit";
import { CameraInterface, Engine } from "../engine/Engine";
import { Line } from "./Line";
import { Point } from "./Point";

const _lineMaterial = new LineBasicMaterial({ color: 0x101010 });

export class Meter extends Line {
  public _meterInput: CSS3DObjectInput;

  private _lineGeometr?: L;
  private _parameters: {
    offset?: Vector3;
    rotation?: Euler;
    scale?: Vector3;
    minScale?: number;
    maxScale?: number;
    reverse?: boolean;
    debug?: boolean;
  } = {
    offset: new Vector3(0.0, 0.0, 0.13),
    rotation: new Euler(0, 0, 0),
    minScale: -1,
    maxScale: -1,
  };
  private _line?: Line;

  constructor(
    line?: Line,
    sPoint?: Point,
    ePoint?: Point,
    options: {
      offset?: Vector3;
      rotation?: Euler;
      scale?: Vector3;
      minScale?: number;
      maxScale?: number;
      reverse?: boolean;
      debug?: boolean;
    } = {
      offset: new Vector3(0.0, 0.0, 0.13),
      rotation: new Euler(0, 0, 0),
      minScale: -1,
      maxScale: -1,
    }
  ) {
    super(sPoint ? sPoint : new Point(), ePoint ? ePoint : new Point());
    this._parameters = { ...this._parameters, ...options };

    this._line = line;
    this._name = "Meter_" + this.name;

    this._meterInput = new CSS3DObjectInput(this.name, {
      minScale: this._parameters.minScale,
      maxScale: this._parameters.maxScale,
      rotation: this._parameters.rotation,
      scale: this.parameters.scale,
      debug: this._parameters.debug,
    });

    this.root?.add(this._meterInput);
    this.root?.addEventListener("added", (e) => {
      this.root?.add(this._meterInput);
    });

    this.root?.addEventListener("removed", (e) => {
      this.root?.remove(this._meterInput);
    });
  }

  private _crateLineGeometry() {
    _lineMaterial.linewidth = 1;

    const points = [];
    points.push(this.point.clone().sub(this.end.point));
    points.push(this.point.clone().sub(this.start.point));

    const bufferPoints = new BufferGeometry().setFromPoints(points);

    const line = new L(bufferPoints, _lineMaterial);
    line.name = "line_of_" + this.name;
    line.renderOrder = 10000;
    return line;
  }

  private _focusOnMenu = true;
  focus(menu: boolean) {
    this._focusOnMenu = menu;
    this._meterInput.dispatchInputEventListener(
      new CustomEvent("focus", { detail: undefined })
    );
  }

  get parameters() {
    return this._parameters;
  }

  get root() {
    if (!this._root) {
      this._root = new Object3D();
      this._root.name = "Meter_" + this._line?.name;
    }
    this._root.clear();

    if (!this._lineGeometr || this.isDirty) {
      this._lineGeometr?.geometry.dispose();
      this._lineGeometr?.removeFromParent();
      this._lineGeometr = this._crateLineGeometry();
    }
    this._root.add(this._lineGeometr);

    this._meterInput.value = formatLength(this._length);
    this._meterInput.name = "textContainer_of_" + this.name;
    this._root?.add(this._meterInput);

    return this._root!;
  }

  removeFromParent(): void {
    super.removeFromParent();
    this.root.removeFromParent();
    Engine.activeEngine.dispatchCustomEvent("request-update");
  }

  render(scene?: Scene, camera?: CameraInterface): void {
    if (this._line) {
      const sP = this._line.start.point;
      const eP = this._line.end.point;
      const ri = this._parameters.reverse ? -1 : 1;
      this.start.point.set(-ri * this._line?.length * 0.5, sP.y, 0.0);
      this.end.point.set(ri * this._line?.length * 0.5, eP.y, 0.0);
    }
    const dirty = this._isDirty;
    super.render(scene, camera);
    this._isDirty = dirty;

    this.root?.position.setZ(this.point.z + this._parameters.offset!.z);
    this.root?.position.setX(this.point.x + this._parameters.offset!.x);
    this.root?.position.setY(this.point.y + this._parameters.offset!.y);
    this._meterInput.value = formatLength(this._length);
    this._meterInput.update();

    Engine.activeEngine.dispatchCustomEvent("request-update");
    this._isDirty = false;
  }

  dispose(scene?: Scene): void {
    super.dispose(scene);
    this.root?.removeFromParent();
    this._root?.traverse((m) => {
      if (m instanceof Mesh) m.geometry.dispose();
    });

    this._meterInput.removeFromParent();
    this._meterInput.dispose();

    Engine.activeEngine.dispatchCustomEvent("request-update");
  }
}
